import { lazy, Suspense, type ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';

// Each portal is its own chunk: its pages load only when a portal login opens it.
const PortalLayout = lazy(() => import('./PortalLayout'));
const OccupierHomePage = lazy(() => import('./pages/OccupierPages').then((m) => ({ default: m.OccupierHomePage })));
const OccupierAccountPage = lazy(() => import('./pages/OccupierPages').then((m) => ({ default: m.OccupierAccountPage })));
const OccupierRequestsPage = lazy(() => import('./pages/OccupierPages').then((m) => ({ default: m.OccupierRequestsPage })));
const OccupierTurnoverPage = lazy(() => import('./pages/OccupierPages').then((m) => ({ default: m.OccupierTurnoverPage })));
const OccupierDocumentsPage = lazy(() => import('./pages/OccupierPages').then((m) => ({ default: m.OccupierDocumentsPage })));
const OccupierServicesPage = lazy(() => import('./pages/OccupierServicesPage'));
const ContractorHomePage = lazy(() => import('./pages/ContractorPages').then((m) => ({ default: m.ContractorHomePage })));
const ContractorWorkPage = lazy(() => import('./pages/ContractorPages').then((m) => ({ default: m.ContractorWorkPage })));
const ContractorInvoicesPage = lazy(() => import('./pages/ContractorPages').then((m) => ({ default: m.ContractorInvoicesPage })));
const ContractorCompanyPage = lazy(() => import('./pages/ContractorPages').then((m) => ({ default: m.ContractorCompanyPage })));
const OwnerHomePage = lazy(() => import('./pages/OwnerPages').then((m) => ({ default: m.OwnerHomePage })));
const OwnerMoneyPage = lazy(() => import('./pages/OwnerPages').then((m) => ({ default: m.OwnerMoneyPage })));
const OwnerApprovalsPage = lazy(() => import('./pages/OwnerPages').then((m) => ({ default: m.OwnerApprovalsPage })));
const OwnerPropertiesPage = lazy(() => import('./pages/OwnerPages').then((m) => ({ default: m.OwnerPropertiesPage })));

const wait = (element: ReactNode) => (
  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>{element}</Suspense>
);

const portal = (kind: 'occupier' | 'contractor' | 'owner', permission: string, children: RouteObject[]): RouteObject => ({
  path: kind,
  element: wait(<RequirePermission permission={permission}><RequireModule module={Modules.Property}><PortalLayout kind={kind} /></RequireModule></RequirePermission>),
  children,
});

/** The three portals (docs §215): signed in, each in its own mobile-first frame, for the Property module and the portal's one right. */
export const portalRoutes: RouteObject[] = [
  portal('occupier', Perm.Property.Occupier, [
    { index: true, element: wait(<OccupierHomePage />) },
    { path: 'account/:view?', element: wait(<OccupierAccountPage />) },
    { path: 'requests/:id?', element: wait(<OccupierRequestsPage />) },
    { path: 'turnover', element: wait(<OccupierTurnoverPage />) },
    { path: 'documents', element: wait(<OccupierDocumentsPage />) },
    { path: 'services', element: wait(<OccupierServicesPage />) },
  ]),
  portal('contractor', Perm.Property.Contractor, [
    { index: true, element: wait(<ContractorHomePage />) },
    { path: 'work/:id?', element: wait(<ContractorWorkPage />) },
    { path: 'invoices', element: wait(<ContractorInvoicesPage />) },
    { path: 'company', element: wait(<ContractorCompanyPage />) },
  ]),
  portal('owner', Perm.Property.Owner, [
    { index: true, element: wait(<OwnerHomePage />) },
    { path: 'money/:view?', element: wait(<OwnerMoneyPage />) },
    { path: 'approvals', element: wait(<OwnerApprovalsPage />) },
    { path: 'properties/:view?', element: wait(<OwnerPropertiesPage />) },
  ]),
];
